import { useMemo } from 'react';
import styles from './TaskModal.module.scss';
import { Text } from '../../typography';
import Modal from '../Modal';
import { useModal } from '../../../providers/ModalProvider';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { modals } from '../../../providers/modals';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import { useWorkspace } from '../../../providers/WorkspaceProvider';
import UserMultiSelector from '../../UserMultiSelector';
import { keys } from '../../../utilities/translator/translation_keys';
import taskImage from '../../../assets/clipboard_task.png';
import {
  allTypesOption,
  defaultAssessmentTypes,
  taskIntervalStringLookup,
} from '../../../constants/strings';
import { useTemplates } from '../../../providers/TemplateProvider';
import { useQueryParams } from '../../../hooks/misc';
import { useCurrentUser } from '../../../providers/UserProvider';
import DefaultDateSelector from '../../DefaultDateSelector';
import {
  recurrencePatterns,
  taskIntervals,
  taskTypes,
} from '../../../utilities/tasks';
import UniversalDropdown from '../../dropdownV2/UniversalDropdown';
import Spacer from '../../typography/Spacer';
import { useTaskMutations } from '../../../hooks/tasks/task_mutations';
import { customColors, sortAlphabeticalOrder } from '../../../utilities';
import { formatTime, getOrdinalWeekdayLabel } from '../../../utilities/time';
import Button from '../../Button';
import TaskVehicles from '../../tasks/TaskVehicles';
import TextWithIcon from '../../typography/TextWithIcon';
import SimpleWorkspaceCard from '../../SimpleWorkspaceCard';
import Icon from '../../Icon';

const assignTaskMutation = loader('./TaskModal.assign.graphql');
const assignRolesToTaskMutation = loader('./TaskModal.assignRole.graphql');
const removeRoleFromTaskMutation = loader('./TaskModal.removeRole.graphql');
const setApplyAllRolesMutation = loader('./TaskModal.setApplyAllRoles.graphql');
const removeUserFromTaskMutation = loader('./TaskModal.removeUser.graphql');
const deleteTaskMutation = loader('./TaskModal.deleteTask.graphql');
const tasksQueryName = 'GetTasks';

export default function TaskModal() {
  const { modalState, closeModal, updateModal, openConfirmationModal } =
    useModal();
  const { inWorkspaces, allVehicles, workspace } = useWorkspace();
  const { t } = useTranslation();
  const { addTask: createTask, editTask, addTaskAssignee } = useTaskMutations();
  const { assessmentName, observationName, assessmentTypes, allRoles } =
    useCurrentUser();
  const [assignTask] = useMutation(assignTaskMutation);
  const [deleteTask] = useMutation(deleteTaskMutation);
  const { allTemplates: templates } = useTemplates();
  const { upsertParam } = useQueryParams();
  const { activeTemplateTypes } = useTemplates();

  const [assignRolesToTask] = useMutation(assignRolesToTaskMutation, {
    refetchQueries: [tasksQueryName],
  });
  const [removeRoleFromTask] = useMutation(removeRoleFromTaskMutation, {
    refetchQueries: [tasksQueryName],
  });
  const [setApplyAllRoles] = useMutation(setApplyAllRolesMutation, {
    refetchQueries: [tasksQueryName],
  });

  const [removeUserFromTask] = useMutation(removeUserFromTaskMutation, {
    refetchQueries: [tasksQueryName],
    update(cache, _, { variables: { userId, id: taskId } }) {
      const normalizedUserId = cache.identify({
        id: userId,
        __typename: 'User',
      });
      cache.modify({
        id: normalizedUserId,
        fields: {
          tasks(existingTasksRefs = [], { readField }) {
            return existingTasksRefs.filter(
              (taskRef) => readField('id', taskRef) !== taskId,
            );
          },
        },
      });
    },
  });

  const update = (variables) =>
    updateModal({
      modalName: modals.task,
      variables: { ...variables },
    });

  const {
    taskId,
    show,
    title,
    template,
    interval = 'ONE_TIME',
    recurrencePattern = 'DATE',
    type,
    subtype,
    assignees,
    selectedRoles = [],
    templateType,
    taskWorkspace = workspace,
    taskType,
    scheduledStartDate = new Date(Date.now() + 86400000),
    scheduledEndDate,
    dueDate,
    customDays,
    vehicles: vehiclesFromState,
    vehicleAssigneeOverrides = {},
    applyAllDirty = false,
    task = {},
    refetch = () => {},
    target,
  } = modalState.taskModal;

  const onHide = () => {
    closeModal({ modalName: modals.task });
    refetch();
  };

  const allWorkspaces =
    taskWorkspace?.id === 'company_shared' || !taskWorkspace?.id;

  const vehicles = useMemo(() => {
    if (vehiclesFromState?.length) return vehiclesFromState;
    if (!!task?.type || type) {
      const t = task?.type || type;
      const s = task?.subtype || subtype;
      const filtered = allVehicles.filter(
        (v) =>
          v.type === t &&
          (!s || v.subtype === s) &&
          (allWorkspaces ||
            v.workspaces?.some((ws) => ws.id === taskWorkspace?.id)),
      );
      return sortAlphabeticalOrder(filtered, 'unitNumber');
    }
    if (!task?.assigneeInfo?.length) return [];
    const seenIds = new Set();
    return task.assigneeInfo
      .filter(({ vehicle }) => {
        if (!vehicle?.id || seenIds.has(vehicle.id)) return false;
        seenIds.add(vehicle.id);
        return true;
      })
      .map(
        ({ vehicle }) =>
          allVehicles.find((v) => v.id === vehicle.id) ?? vehicle,
      );
  }, [
    task?.type,
    task?.assigneeInfo,
    task?.subtype,
    type,
    vehiclesFromState,
    subtype,
    allVehicles,
    allWorkspaces,
    taskWorkspace?.id,
  ]);

  const resolvedOverrides = useMemo(() => {
    if (Object.keys(vehicleAssigneeOverrides).length)
      return vehicleAssigneeOverrides;
    if (!task?.assigneeInfo?.length) return {};
    return task.assigneeInfo.reduce((acc, { assignee, vehicle }) => {
      if (!vehicle?.id) return acc;
      return { ...acc, [vehicle.id]: [...(acc[vehicle.id] ?? []), assignee] };
    }, {});
  }, [vehicleAssigneeOverrides, task]);

  const isEdit = !!taskId;
  const taskTypeValue =
    typeof taskType === 'string' ? taskType : taskType?.value ?? 'INSPECTION';

  const assignedVehicleCount = useMemo(
    () =>
      (vehicles ?? []).filter((v) => resolvedOverrides[v.id]?.length > 0)
        .length,
    [vehicles, resolvedOverrides],
  );
  const noVehicleAssignee =
    taskTypeValue === 'VEHICLE' &&
    (isEdit
      ? assignedVehicleCount === 0 && !selectedRoles?.length
      : !vehicles?.length ||
        (assignedVehicleCount === 0 && !selectedRoles?.length));

  const fetchSubmitDisabled = () => {
    switch (taskTypeValue) {
      case 'INSPECTION':
      case 'VEHICLE':
        return !title || !template || !interval || noVehicleAssignee;
      case 'OBSERVATION':
        return !title || !interval;
      default:
        return true;
    }
  };
  const submitDisabled = fetchSubmitDisabled();
  const taskOptions = taskTypes({ assessmentName, observationName });
  const selectableVehicles = useMemo(
    () =>
      allWorkspaces
        ? allVehicles
        : allVehicles.filter((v) =>
            v.workspaces?.some((ws) => ws.id === taskWorkspace?.id),
          ),
    [allWorkspaces, allVehicles, taskWorkspace?.id],
  );
  const templateOptions = useMemo(() => {
    const temp = templates.filter(
      (t) =>
        t.templateType !== 'OBSERVATION' && !t.isArchived && !t.internalType,
    );
    const t = allWorkspaces
      ? temp.filter((t) => !t.workspace?.id)
      : temp.filter(
          (t) => !t.workspace?.id || t.workspace?.id === taskWorkspace?.id,
        );
    return templateType?.key !== 'ALL_TYPES'
      ? t.filter((t) => t.templateType === templateType?.key)
      : t;
  }, [allWorkspaces, taskWorkspace?.id, templateType, templates]);

  return (
    <Modal
      footer={
        taskTypeValue === 'VEHICLE' ? (
          <Text
            noMargin
            size="sm"
            weight="semiBold"
            color={noVehicleAssignee ? 'red' : 'secondary'}
          >
            {!vehicles?.length
              ? 'Select at least one vehicle.'
              : noVehicleAssignee
              ? 'Assign at least one user or role to create this task.'
              : `${assignedVehicleCount} ${
                  assignedVehicleCount === 1 ? 'vehicle' : 'vehicles'
                } assigned`}
          </Text>
        ) : assignees?.length ? (
          <Text noMargin size="sm" color="secondary">
            {`${assignees.length} ${
              assignees.length === 1 ? 'assignee' : 'assignees'
            } assigned`}
          </Text>
        ) : null
      }
      open={show || false}
      onClose={onHide}
      variant="primary"
      onDelete={() => {
        openConfirmationModal({
          variant: 'danger',
          title: 'Delete Task',
          description: `Are you sure you want to delete ${
            title || 'this task'
          }? This action cannot be undone.`,
          onSubmit: () => {
            deleteTask({ variables: { id: taskId } }).then(onHide);
          },
        });
      }}
      title={
        isEdit
          ? t(keys.action.EDIT_VARIABLE, { variable: `${t(keys.tasks.TASK)}` })
          : t(keys.action.CREATE_VARIABLE, { variable: t(keys.tasks.TASK) })
      }
      submitDisabled={submitDisabled}
      titleImage={
        <img className={styles.taskImage} src={taskImage} alt="task_png" />
      }
      hideDelete={!taskId}
      submitText={isEdit ? t(keys.action.SAVE) : t(keys.action.SUBMIT)}
      onSubmit={() => {
        const shared = {
          title,
          taskType: taskTypeValue,
          templateId: template?.id ?? null,
          interval: interval || 'ONE_TIME',
          recurrencePattern:
            interval === 'MONTHLY' || interval === 'ANNUALLY'
              ? recurrencePattern
              : null,
          workspaceId:
            !taskWorkspace?.id || taskWorkspace?.id === 'company_shared'
              ? null
              : taskWorkspace?.id,
          assigneeIds:
            taskTypeValue === 'VEHICLE'
              ? []
              : assignees?.map(({ id }) => id) ?? [],
          roleIds:
            taskTypeValue === 'VEHICLE'
              ? []
              : selectedRoles?.map(({ id }) => id) ?? [],
          scheduledStartDate:
            interval === 'ONE_TIME'
              ? dueDate ?? scheduledStartDate
              : scheduledStartDate,
          scheduledEndDate: interval === 'ONE_TIME' ? null : scheduledEndDate,
          dueDate: interval === 'ONE_TIME' ? dueDate : null,
          type,
          subtype,
          target: interval === 'DAILY' ? target || 7 : null,
          customDays,
        };
        if (isEdit) {
          editTask({ id: taskId, ...shared }).then(() => {
            if (taskTypeValue === 'VEHICLE' && selectedRoles?.length > 0) {
              setApplyAllRoles({
                variables: {
                  taskId,
                  roleIds: selectedRoles.map(({ id }) => id),
                  vehicleIds: vehicles?.map(({ id }) => id) ?? [],
                },
              }).then(() => onHide());
            } else {
              onHide();
            }
          });
        } else {
          createTask(shared).then((newTask) => {
            if (taskTypeValue === 'VEHICLE' && newTask?.id) {
              const assignments = (vehicles ?? []).flatMap((v) =>
                (resolvedOverrides[v.id] ?? []).map((u) => ({
                  vehicleId: v.id,
                  userId: u.id,
                })),
              );
              Promise.all(
                assignments.map(({ vehicleId, userId }) =>
                  addTaskAssignee({ taskId: newTask.id, vehicleId, userId }),
                ),
              ).then(() => {
                if (selectedRoles?.length > 0) {
                  setApplyAllRoles({
                    variables: {
                      taskId: newTask.id,
                      roleIds: selectedRoles.map(({ id }) => id),
                      vehicleIds: vehicles?.map(({ id }) => id) ?? [],
                    },
                  }).then(() => onHide());
                } else {
                  onHide();
                }
              });
            } else {
              onHide();
            }
          });
        }
      }}
    >
      <div className={styles.content}>
        <div className={styles.field}>
          <Text noMargin weight="semibold">
            Task Type<span className={styles.red}>*</span>
          </Text>
          {taskType === 'VEHICLE' && (task?.type || type) ? (
            <div className={styles.taskType}>
              <TextWithIcon
                weight="semiBold"
                leftIcon="handyman"
                iconProps={{
                  fontSize: '1.2rem',
                  color: customColors.SECONDARY,
                }}
                noMargin
              >{`Assets: ${task?.type || type} ${
                !!subtype ? `- ${subtype}` : ''
              } `}</TextWithIcon>
            </div>
          ) : !!taskId ? (
            <TextWithIcon
              weight="semiBold"
              leftIcon={taskOptions[taskType ?? taskType?.value]?.icon}
            >{`${
              taskOptions[taskType ?? taskType?.value]?.title
            }`}</TextWithIcon>
          ) : (
            <UniversalDropdown
              readOnly={!!taskId || !!type || task?.type}
              options={Object.values(taskOptions)}
              onChange={(taskType) => {
                update({ taskType });
                upsertParam('tab', taskType.value);
              }}
              selected={
                taskOptions[
                  typeof taskType === 'string' ? taskType : taskType?.value
                ] || taskOptions.INSPECTION
              }
            />
          )}
        </div>
        <div className={styles.field}>
          <Text noMargin weight="semiBold">
            {t(keys.common.WORKSPACE)}
            <span className={styles.red}>*</span>
          </Text>
          {!!taskId ? (
            <SimpleWorkspaceCard workspace={taskWorkspace} />
          ) : (
            <UniversalDropdown
              isClearable={false}
              selected={taskWorkspace}
              type="workspace"
              options={inWorkspaces || []}
              onChange={(ws) => {
                update({
                  taskWorkspace: ws,
                  templateType: defaultAssessmentTypes.fleet,
                  template: null,
                  assignees: [],
                  vehicles: [],
                });
              }}
            />
          )}
        </div>
        <div className={styles.field}>
          <Text className={styles.text} noMargin weight="semiBold">
            {t(keys.tasks.NAME)}
            <span className={styles.red}>*</span>
          </Text>
          <Form.Control
            className={!!title ? undefined : styles.highlight}
            type="text"
            placeholder={t(keys.tasks.ENTER_NAME)}
            value={title}
            onChange={({ target: { value } }) => update({ title: value })}
          />
        </div>
        {taskTypeValue !== 'OBSERVATION' && (
          <>
            <div className={styles.field}>
              <Text noMargin weight="semibold">
                Template Type<span className={styles.red}>*</span>
              </Text>
              <UniversalDropdown
                disabled={!type}
                selected={templateType}
                options={[
                  allTypesOption,
                  ...(Object.values(assessmentTypes).filter(
                    (a) =>
                      a.key !== 'OBSERVATION' &&
                      !a.internalType &&
                      activeTemplateTypes.includes(a.key),
                  ) || []),
                ]}
                onChange={(newType) => {
                  update({ templateType: newType, template: null });
                }}
                type="templateType"
              />
            </div>
            <div className={styles.field}>
              <Text noMargin weight="semiBold">
                {t(keys.common.TEMPLATE)}
                <span className={styles.red}>*</span>
              </Text>
              <UniversalDropdown
                isRequired={!template?.id}
                selected={template}
                disabled={!type}
                options={templateOptions || []}
                onChange={(newTemplate) => {
                  const matchedType = newTemplate
                    ? Object.values(assessmentTypes).find(
                        (a) => a.key === newTemplate.templateType,
                      ) ?? templateType
                    : templateType;
                  update({ template: newTemplate, templateType: matchedType });
                }}
                type="template"
                placeholder={'Select Template...'}
              />
            </div>
          </>
        )}

        <div className={styles.field}>
          <Text noMargin weight="semiBold">
            {t(keys.tasks.INTERVAL)}
            <span className={styles.red}>*</span>
          </Text>
          <div className={styles.flexRowRight}>
            <Form.Group className={styles.flexRow}>
              {Object.values(taskIntervals)
                .filter((t) => t?.show !== false)
                .map((i, idx) => (
                  <div className={styles.flexRow} key={`${i.value}-${idx}`}>
                    <Form.Check
                      type="radio"
                      checked={
                        interval === i.value || interval === `${i.value}_5`
                      }
                      value={i.value}
                      onChange={({ target: { value } }) =>
                        update({ interval: value })
                      }
                    />
                    <Text noMargin>
                      {t(taskIntervalStringLookup[i.value]) || i.label}
                    </Text>
                  </div>
                ))}
            </Form.Group>
          </div>
        </div>
        {interval === 'DAILY' && (
          <div>
            <Form.Group className={styles.flexRowRight}>
              <Form.Check
                type="radio"
                checked={target === 7 || !target}
                value={taskIntervals.DAILY.value}
                onChange={() => {
                  update({ target: 7 });
                }}
              />
              <Text noMargin>7 days (Mon-Sun)</Text>
              <Spacer />
              <Form.Check
                type="radio"
                checked={target === 5}
                onChange={() => {
                  update({ target: 5 });
                }}
              />
              <Text noMargin>{'5 days (Mon-Fri)'}</Text>
            </Form.Group>
          </div>
        )}
        {interval === 'CUSTOM' && (
          <div className={styles.flexRowRight}>
            <Form.Control
              type="number"
              className={styles.dayInput}
              min="1"
              placeholder="Enter # Days"
              value={customDays}
              onChange={({ target: { value } }) =>
                update({ customDays: value })
              }
            />
          </div>
        )}
        {(interval === 'MONTHLY' || interval === 'ANNUALLY') && (
          <div className={styles.field}>
            <Text noMargin weight="semiBold">
              Repeat On
            </Text>
            <Form.Group className={styles.flexRowRight}>
              <Form.Check
                type="radio"
                checked={recurrencePattern !== 'WEEKDAY'}
                onChange={() => update({ recurrencePattern: 'DATE' })}
              />
              <Text noMargin>{recurrencePatterns.DATE.label}</Text>
              <Spacer />
              <Form.Check
                type="radio"
                checked={recurrencePattern === 'WEEKDAY'}
                onChange={() => update({ recurrencePattern: 'WEEKDAY' })}
              />
              <Text noMargin>
                {`Same weekday each period (${getOrdinalWeekdayLabel(
                  scheduledStartDate,
                )})`}
              </Text>
            </Form.Group>
          </div>
        )}
        <div className={styles.field}>
          {interval === 'ONE_TIME' ? (
            <>
              <Text noMargin weight="semiBold">
                Due Date
              </Text>
              <DefaultDateSelector
                value={dueDate ?? ''}
                onChange={(e) => update({ dueDate: e.target.value || null })}
                className={styles.datePicker}
                min={new Date().toISOString()}
              />
            </>
          ) : (
            <>
              <Text noMargin weight="semiBold">
                Start-End Date
              </Text>
              <div className={styles.header}>
                {!!taskId && new Date(scheduledStartDate) < new Date() ? (
                  <div className={styles.dateText}>
                    <div className={styles.header}>
                      <Text weight="semiBold" noMargin>
                        {`${
                          !!scheduledStartDate
                            ? `${formatTime(scheduledStartDate)} `
                            : ''
                        }(Started)`}
                      </Text>
                      <Icon style={{ fontSize: '1rem' }}>event_available</Icon>
                    </div>
                  </div>
                ) : (
                  <DefaultDateSelector
                    value={scheduledStartDate ?? new Date().toISOString()}
                    onChange={(e) =>
                      update({ scheduledStartDate: e.target.value })
                    }
                    className={styles.datePicker}
                    min={new Date().toISOString()}
                  />
                )}
                <span>-</span>
                <DefaultDateSelector
                  min={scheduledStartDate ?? new Date().toISOString()}
                  value={scheduledEndDate ?? ''}
                  onChange={(e) =>
                    update({ scheduledEndDate: e.target.value || null })
                  }
                  className={styles.datePicker}
                />
              </div>
            </>
          )}
        </div>

        {taskTypeValue !== 'VEHICLE' ? (
          <>
            <Text marginXs noMargin weight="semiBold">
              {t(keys.common.ASSIGNEES)}
            </Text>
            <UniversalDropdown
              isMulti
              closeMenuOnSelect={false}
              placeholder="Assign by Role (optional)..."
              options={allRoles}
              selected={selectedRoles}
              onChange={(roles) => {
                const incoming = roles ?? [];
                if (taskId) {
                  const addedRole = incoming.find(
                    (r) => !selectedRoles?.some((sr) => sr.id === r.id),
                  );
                  const removedRole = selectedRoles?.find(
                    (sr) => !incoming.some((r) => r.id === sr.id),
                  );
                  const toUniqueAssignees = (assigneeInfo) => [
                    ...new Map(
                      (assigneeInfo ?? [])
                        .map(({ assignee }) => assignee)
                        .filter(Boolean)
                        .map((u) => [u.id, u]),
                    ).values(),
                  ];

                  if (addedRole) {
                    assignRolesToTask({
                      variables: { id: taskId, roleIds: [addedRole.id] },
                    }).then(({ data }) => {
                      const updated = toUniqueAssignees(
                        data?.updateTask?.assigneeInfo,
                      );
                      if (updated.length) update({ assignees: updated });
                    });
                  }
                  if (removedRole) {
                    removeRoleFromTask({
                      variables: { taskId, roleId: removedRole.id },
                    }).then(({ data }) => {
                      const updated = toUniqueAssignees(
                        data?.removeRoleFromTask?.assigneeInfo,
                      );
                      update({ assignees: updated });
                    });
                  }
                }
                update({ selectedRoles: incoming });
              }}
              type="role"
            />
            <Spacer size="xs" />
            <UserMultiSelector
              type={
                taskWorkspace?.id === 'company_shared' ? 'all' : 'workspace'
              }
              otherWorkspace={taskWorkspace}
              selected={assignees}
              stateUpdateOnly
              setUsers={(assignees) => update({ assignees })}
              onUserAdded={(user) => {
                if (!user?.id) return;
                if (taskId) {
                  const userHasSimilarTask = user?.tasks?.some(
                    ({ templateId, workspaceId }) =>
                      templateId === template.id &&
                      workspaceId === taskWorkspace?.id,
                  );

                  if (userHasSimilarTask) {
                    const similarTask = user.tasks.find(
                      ({ templateId, workspaceId }) =>
                        templateId === template.id &&
                        workspaceId === taskWorkspace?.id,
                    );
                    const name = user.firstName;

                    openConfirmationModal({
                      title: t(keys.common.HOLD_ON),
                      highlight: true,
                      wordsToHighlight: [name, similarTask.title, title].map(
                        (item) => item.toLowerCase(),
                      ),
                      description: t(keys.tasks.REASSIGN, {
                        name,
                        similarTask: similarTask.title,
                        title,
                      }),
                      variant: 'warning',
                      buttonText: t(keys.common.YES),
                      onSubmit: () => {
                        removeUserFromTask({
                          variables: { id: similarTask.id, userId: user.id },
                        })
                          .then(() =>
                            assignTask({
                              variables: { id: taskId, assigneeIds: [user.id] },
                            }),
                          )
                          .then(() =>
                            update({ assignees: [...assignees, user] }),
                          );
                      },
                    });
                  } else {
                    assignTask({
                      variables: { id: taskId, assigneeIds: [user.id] },
                    }).then(() => update({ assignees: [...assignees, user] }));
                  }
                } else {
                  update({ assignees: [...assignees, user] });
                }
              }}
              onUserRemoved={(user) =>
                taskId
                  ? removeUserFromTask({
                      variables: { id: taskId, userId: user.id },
                    }).then(() =>
                      update({
                        assignees: assignees.filter(({ id }) => id !== user.id),
                      }),
                    )
                  : update({
                      assignees: assignees.filter(({ id }) => id !== user.id),
                    })
              }
            />
          </>
        ) : (
          <>
            <Text noMargin weight="semibold">
              {t(keys.common.ASSETS)}
            </Text>
            <div className={styles.sectionLine} />
            <Text marginXs noMargin weight="semiBold">
              Apply to All Assets
            </Text>
            <div className={styles.applyAllRow}>
              <div className={styles.applyAllSelector}>
                <UniversalDropdown
                  isMulti
                  closeMenuOnSelect={false}
                  placeholder="Assign role(s) to all assets..."
                  options={allRoles}
                  selected={selectedRoles}
                  onChange={(roles) => {
                    const incoming = roles ?? [];
                    update({
                      selectedRoles: incoming,
                      applyAllDirty: !!taskId,
                    });
                  }}
                  type="role"
                />
              </div>
              {!!taskId && (
                <Button
                  value="Save"
                  size="sm"
                  disabled={!applyAllDirty}
                  onClick={() => {
                    setApplyAllRoles({
                      variables: {
                        taskId,
                        roleIds: selectedRoles.map(({ id }) => id),
                        vehicleIds: vehicles?.map(({ id }) => id) ?? [],
                      },
                    }).then(() => update({ applyAllDirty: false }));
                  }}
                />
              )}
            </div>
            {!task?.type && !type && (
              <UniversalDropdown
                placeholder={'Select Asset(s)...'}
                isMulti
                closeMenuOnSelect={false}
                disabled={!type}
                selected={vehicles}
                options={selectableVehicles}
                onChange={(newVehicles) => {
                  const keptIds = new Set(newVehicles?.map((v) => v.id) ?? []);
                  const fullVehicles = (newVehicles ?? []).map(
                    (v) => allVehicles.find((av) => av.id === v.id) ?? v,
                  );
                  const pruned = Object.fromEntries(
                    Object.entries(vehicleAssigneeOverrides).filter(([id]) =>
                      keptIds.has(id),
                    ),
                  );
                  update({
                    vehicles: fullVehicles,
                    vehicleAssigneeOverrides: pruned,
                  });
                }}
                type="asset"
              />
            )}
            {!!vehicles?.length ? (
              <div>
                <TaskVehicles
                  type={task?.type ?? null}
                  subtype={task?.subtype ?? null}
                  vehicles={vehicles}
                  workspace={taskWorkspace}
                  vehicleAssigneeOverrides={resolvedOverrides}
                  onUserAdded={(vehicleId, user, current) => {
                    update({
                      vehicleAssigneeOverrides: {
                        ...resolvedOverrides,
                        [vehicleId]: [...current, user],
                      },
                    });
                    if (taskId) {
                      addTaskAssignee({ userId: user.id, vehicleId, taskId });
                    }
                  }}
                  onUserRemoved={(vehicleId, user, current) => {
                    update({
                      vehicleAssigneeOverrides: {
                        ...resolvedOverrides,
                        [vehicleId]: current.filter((u) => u.id !== user.id),
                      },
                    });
                    if (taskId) {
                      removeUserFromTask({
                        variables: { id: taskId, userId: user.id, vehicleId },
                      });
                    }
                  }}
                />
              </div>
            ) : (
              task?.type ||
              (type && (
                <div>
                  <Spacer />
                  <Text textAlign="center" weight="semiBold">
                    {`No ${type || ''} ${
                      subtype ?? `${subtype} || ''}`
                    } Found ${
                      taskWorkspace?.id ? `in ${taskWorkspace.title}` : ''
                    } `}
                  </Text>
                </div>
              ))
            )}
            <Spacer />
          </>
        )}
      </div>
      <br />
      <br />
    </Modal>
  );
}
